import React from 'react';
import { isEmpty } from 'lodash';
import { companySummaryUrl } from 'common/config/urls';
import { Row, Rows } from 'common/types/scalarSpreadsheet';
import { ValueWithLinkViewer } from 'components';
import { getFundInvestmentsSummaryUrl } from 'pages/FundInvestments/common/urls';
import {
  FUND_SUMMARY_SPREADSHEET_COLUMN_KEY_PREFIX,
  FUND_SUMMARY_SPREADSHEET_TABLE,
  SHEET_ALIASES_CONSTANTS,
  SHEET_TITLES_CONSTANTS,
} from 'pages/Funds/common/constants/fundSummary';
import { getExchangeRateTooltip } from 'pages/Funds/utils';
import { generateColumnKey, getArrayValue, getNumberValue, getStringValue } from 'utilities';
import { CreateCompaniesRowsParams, CreateFundInvestmentRowsParams, CreateRowsParams } from './types';

const createCompaniesRows = (params: CreateCompaniesRowsParams): Rows => {
  const { areFundInvestmentsEnabled, companies, firmSlug, queryParams, shouldIncludeGlobalTotal } = params;

  const companyRows = getArrayValue(
    companies?.map(company => {
      const companyCaptableCurrency = getStringValue(company?.company_captable_currency?.toString());
      const companyCaptableId = getNumberValue(company?.cap_table);
      const companySlug = getStringValue(company?.company_slug);
      let companyName = getStringValue(company?.company_name);

      // Display Company Captable Currency if it's different from Fund currency
      if (company.fund_currency !== company.company_captable_currency) {
        companyName = `${companyName} (${companyCaptableCurrency})`;
      }

      // Get Company Summary URL
      const companyUrl = `${companySummaryUrl(firmSlug, companySlug)}${queryParams}`;
      const fundCurrencyValue = getStringValue(company.fund_currency?.toString());
      const exchangeRateTooltip = getExchangeRateTooltip(
        fundCurrencyValue,
        companyCaptableCurrency,
        Number(company.exchange_rate_to_fund_currency ?? 1)
      );

      return {
        alias: generateColumnKey({
          id: companyCaptableId,
          name: companySlug,
          prefix: FUND_SUMMARY_SPREADSHEET_COLUMN_KEY_PREFIX,
        }),
        gridType: 'string',
        readOnly: true,
        value: <ValueWithLinkViewer exchangeRateMessage={exchangeRateTooltip} name={companyName} url={companyUrl} />,
      } as Row;
    })
  );

  if (isEmpty(companyRows)) {
    return [];
  }

  if (areFundInvestmentsEnabled) {
    companyRows.unshift({
      alias: SHEET_ALIASES_CONSTANTS.FUND_SUMMARY_SPREADSHEET_SUBHEADER_TITLE,
      className: `${FUND_SUMMARY_SPREADSHEET_TABLE}__subheader subheader-division`,
      gridType: 'string',
      hidden: true,
      readOnly: true,
      value: (
        <ValueWithLinkViewer
          isDarkMode
          name={SHEET_TITLES_CONSTANTS.FUND_SUMMARY_SPREADSHEET_DIRECT_INVESTMENTS_TITLE}
        />
      ),
    } as Row);
  }

  companyRows.push({
    alias: SHEET_ALIASES_CONSTANTS.FUND_SUMMARY_SPREADSHEET_DIRECT_INVESTMENTS_TOTAL,
    className: `${FUND_SUMMARY_SPREADSHEET_TABLE}__subtotal division`,
    format: null,
    gridType: 'string',
    readOnly: true,
    value: shouldIncludeGlobalTotal
      ? SHEET_TITLES_CONSTANTS.FUND_SUMMARY_SPREADSHEET_DIRECT_INVESTMENTS_TOTAL_TITLE
      : SHEET_TITLES_CONSTANTS.FUND_SUMMARY_SPREADSHEET_TOTAL_TITLE,
  } as Row);

  return companyRows;
};

const createFundInvestmentRows = (params: CreateFundInvestmentRowsParams): Rows => {
  const { areFundInvestmentsEnabled, firmSlug, fundInvestments, queryParams, shouldIncludeGlobalTotal } = params;

  const fundInvesmentRows = getArrayValue(
    fundInvestments?.map(fundInvestment => {
      const companyCaptableCurrency = getStringValue(fundInvestment?.company_captable_currency?.toString());
      const id = getNumberValue(fundInvestment?.id);
      const companySlug = getStringValue(fundInvestment?.company_slug);
      let companyName = getStringValue(fundInvestment?.company_name);

      // Display Company Captable Currency if it's different from Fund currency
      if (fundInvestment.fund_currency !== fundInvestment.company_captable_currency) {
        companyName = `${companyName} (${companyCaptableCurrency})`;
      }

      // Get Fund Investment Summary URL
      const companyUrl = `${getFundInvestmentsSummaryUrl(firmSlug, companySlug)}${queryParams}`;
      const fundCurrencyValue = getStringValue(fundInvestment.fund_currency?.toString());
      const exchangeRateTooltip = getExchangeRateTooltip(
        fundCurrencyValue,
        companyCaptableCurrency,
        Number(fundInvestment.exchange_rate_to_fund_currency ?? 1)
      );

      return {
        alias: generateColumnKey({
          id,
          name: companySlug,
          prefix: FUND_SUMMARY_SPREADSHEET_COLUMN_KEY_PREFIX,
        }),

        gridType: 'string',
        readOnly: true,
        value: <ValueWithLinkViewer exchangeRateMessage={exchangeRateTooltip} name={companyName} url={companyUrl} />,
      } as Row;
    })
  );

  if (!areFundInvestmentsEnabled || isEmpty(fundInvesmentRows)) {
    return [];
  }

  fundInvesmentRows.unshift({
    alias: SHEET_ALIASES_CONSTANTS.FUND_SUMMARY_SPREADSHEET_SUBHEADER_TITLE,
    className: `${FUND_SUMMARY_SPREADSHEET_TABLE}__subheader subheader-division`,
    gridType: 'string',
    hidden: true,
    readOnly: true,
    value: (
      <ValueWithLinkViewer isDarkMode name={SHEET_TITLES_CONSTANTS.FUND_SUMMARY_SPREADSHEET_FUND_INVESTMENTS_TITLE} />
    ),
  } as Row);

  fundInvesmentRows.push({
    alias: SHEET_ALIASES_CONSTANTS.FUND_SUMMARY_SPREADSHEET_FUND_INVESTMENTS_TOTAL,
    className: `${FUND_SUMMARY_SPREADSHEET_TABLE}__subtotal division`,
    format: null,
    gridType: 'string',
    readOnly: true,
    value: shouldIncludeGlobalTotal
      ? SHEET_TITLES_CONSTANTS.FUND_SUMMARY_SPREADSHEET_FUND_INVESTMENTS_TOTAL_TITLE
      : SHEET_TITLES_CONSTANTS.FUND_SUMMARY_SPREADSHEET_TOTAL_TITLE,
  } as Row);

  return fundInvesmentRows;
};

const createRows = (params: CreateRowsParams): Rows => {
  const {
    areFundInvestmentsEnabled,
    companies,
    companyCount,
    firmInfo,
    fundInvestmentCount,
    fundInvestments,
    measurementDate,
  } = params;

  const firmSlug = getStringValue(firmInfo?.slug);
  const queryParams = measurementDate?.slug ? `?date=${measurementDate?.slug}` : '';

  const shouldIncludeGlobalTotal = companyCount > 0 && fundInvestmentCount > 0;

  const rows = [
    {
      alias: SHEET_ALIASES_CONSTANTS.FUND_SUMMARY_SPREADSHEET_HEADER_TITLE,
      className: `${FUND_SUMMARY_SPREADSHEET_TABLE}-header division-bottom-only`,
      gridType: 'string',
      largeHeader: true,
      readOnly: true,
      value: SHEET_TITLES_CONSTANTS.FUND_SUMMARY_SPREADSHEET_HEADERS[0].value,
    } as Row,
    ...createCompaniesRows({ areFundInvestmentsEnabled, companies, firmSlug, queryParams, shouldIncludeGlobalTotal }),
    ...createFundInvestmentRows({
      areFundInvestmentsEnabled,
      firmSlug,
      fundInvestments,
      queryParams,
      shouldIncludeGlobalTotal,
    }),
  ];

  if (areFundInvestmentsEnabled && shouldIncludeGlobalTotal) {
    rows.push({
      alias: SHEET_ALIASES_CONSTANTS.FUND_SUMMARY_SPREADSHEET_TOTAL,
      className: `${FUND_SUMMARY_SPREADSHEET_TABLE}-total division`,
      format: null,
      gridType: 'string',
      readOnly: true,
      value: SHEET_TITLES_CONSTANTS.FUND_SUMMARY_SPREADSHEET_TOTAL_TITLE,
    } as Row);
  }

  return rows;
};

export default createRows;
